import React from "react";
import { X, Heart } from "lucide-react";
import { K } from "../theme";
import { Av } from "./Avatar";
import { Bt } from "./Button";

// Opens when the person who sent a wish clicks "<name> says thank you…" in their bell — the other end of the
// Thank You button on WishReceivedModal. Same colours per occasion, with hearts floating up and a bouncing 🙏.
const TYPE_META = {
  birthday:    { what: "birthday wishes",          emoji: "🎂", grad: `linear-gradient(135deg, ${K.ro} 0%, #e11d48 100%)`, c: K.ro },
  anniversary: { what: "work anniversary wishes",  emoji: "🎉", grad: `linear-gradient(135deg, ${K.pu} 0%, #7c3aed 100%)`, c: K.pu },
  eom:         { what: "congratulations",          emoji: "🏆", grad: `linear-gradient(135deg, ${K.a} 0%, #b45309 100%)`, c: K.a },
};

// Deterministic so the hearts don't jump on re-render.
const HEARTS = Array.from({ length: 16 }, (_, i) => ({
  left: 4 + ((i * 29) % 92),
  delay: ((i * 37) % 30) / 10,
  dur: 3 + ((i * 13) % 20) / 10,
  size: 10 + (i % 4) * 4,
  op: 0.35 + (i % 3) * 0.2,
}));

export const ThankYouModal = ({ item, onClose }) => {
  if (!item) return null;
  const meta = TYPE_META[item.type] || TYPE_META.birthday;
  return (
    <div className="fixed inset-0 animate-fadeIn bg-black/50 backdrop-blur-sm z-[260] flex items-center justify-center p-4" onMouseDown={onClose}>
      <div className="animate-modalIn bg-bg1 border border-b0 rounded-2xl w-full max-w-[420px] overflow-hidden shadow-2xl" onMouseDown={(e) => e.stopPropagation()}>
        <div className="relative p-7 text-white text-center overflow-hidden" style={{ background: meta.grad }}>
          <style>{`@keyframes tyRise { 0% { transform: translateY(40px) scale(.6); opacity: 0; } 15% { opacity: 1; } 100% { transform: translateY(-230px) scale(1.15); opacity: 0; } }
@keyframes tyHands { 0% { transform: scale(0) rotate(-25deg); opacity: 0; } 55% { transform: scale(1.25) rotate(8deg); opacity: 1; } 75% { transform: scale(.95) rotate(-4deg); } 100% { transform: scale(1) rotate(0); opacity: 1; } }
@keyframes tyRing { 0% { transform: scale(.9); opacity: .6; } 100% { transform: scale(1.7); opacity: 0; } }
@keyframes tyUp { 0% { transform: translateY(10px); opacity: 0; } 100% { transform: translateY(0); opacity: 1; } }`}</style>
          {HEARTS.map((h, i) => (
            <Heart key={i} className="absolute bottom-0 pointer-events-none" fill="#fff" color="#fff"
              style={{ left: `${h.left}%`, width: h.size, height: h.size, opacity: 0, animation: `tyRise ${h.dur}s ease-out ${h.delay}s infinite`, ["--o"]: h.op }} />
          ))}
          <div className="absolute -right-8 -top-8 w-32 h-32 rounded-full bg-white/10" />
          <div className="absolute -left-8 -bottom-8 w-28 h-28 rounded-full bg-white/10" />
          <button onClick={onClose} className="absolute top-3 right-3 text-white/80 hover:text-white bg-black/20 hover:bg-black/40 rounded-full p-1 border-0 cursor-pointer transition-colors z-10">
            <X size={16} />
          </button>

          <div className="relative inline-block mb-3">
            <span className="absolute inset-0 rounded-full border-2 border-white/70" style={{ animation: "tyRing 1.8s ease-out infinite" }} />
            <span className="absolute inset-0 rounded-full border-2 border-white/50" style={{ animation: "tyRing 1.8s ease-out .9s infinite" }} />
            <div className="relative w-16 h-16 rounded-full p-[3px] bg-white/25 backdrop-blur-md shadow-lg mx-auto">
              <div className="w-full h-full rounded-full bg-white flex items-center justify-center overflow-hidden">
                <Av nm={item.name || "Colleague"} sz={58} rn={meta.c} src={item.photo} />
              </div>
            </div>
            <div className="absolute -bottom-2 -right-3 text-[26px] leading-none" style={{ animation: "tyHands .8s cubic-bezier(.2,.9,.3,1.3) .2s both" }}>🙏</div>
          </div>

          <div className="relative text-[15px] font-bold m-0 truncate max-w-[300px] mx-auto" style={{ animation: "tyUp .5s ease-out .35s both" }}>{item.name || "A colleague"}</div>
          <div className="relative text-white/90 text-[14px] mt-1" style={{ animation: "tyUp .5s ease-out .5s both" }}>says thank you! 💛</div>
        </div>

        <div className="p-6 text-center">
          <div className="text-[34px] leading-none mb-2" style={{ animation: "tyHands .7s cubic-bezier(.2,.9,.3,1.3) .5s both" }}>{meta.emoji}</div>
          <div className="text-[14px] text-t2 leading-relaxed" style={{ animation: "tyUp .5s ease-out .65s both" }}>
            Your {meta.what} made their day — they wanted you to know it reached them.
          </div>
          <Bt full style={{ background: meta.c, marginTop: 20 }} onClick={onClose}>You're welcome 💛</Bt>
        </div>
      </div>
    </div>
  );
};
